import type { DashboardsWidget } from '@smarthome/api';
import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';

import { useDashboardTimeRange } from './DashboardTimeRangeContext';

import { cn, ErrorBoundary } from '@/components/ui';

const GRAPH_TIME_RANGE_LABELS: Record<string, string> = {
  '1h': '1hr',
  '6h': '6hr',
  '24h': '24hr',
  '7d': '7d',
  '30d': '30d',
};

const UTILITY_PRESET_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': '7d',
  '30d': '30d',
  mtd: 'MTD',
  ytd: 'YTD',
  lastWeek: 'Last week',
  lastMonth: 'Last month',
};

function getEffectiveTimeRange(
  widget: DashboardsWidget,
  dashboardTimeRange: string
): string | null {
  const type = widget.type;

  if (type === 'line-graph' || type === 'histogram') {
    const config = widget.config as
      | { timeRangeMode?: string; timeRange?: string }
      | null
      | undefined;
    const mode = config?.timeRangeMode ?? 'fixed';
    const timeRange = mode === 'dashboard' ? dashboardTimeRange : config?.timeRange;
    if (!timeRange) return null;
    return GRAPH_TIME_RANGE_LABELS[timeRange] ?? timeRange;
  }

  if (type === 'utilities-stat' || type === 'utilities-histogram') {
    const config = widget.config as
      | {
          timeRangeMode?: string;
          preset?: string;
        }
      | null
      | undefined;
    const mode = config?.timeRangeMode ?? 'fixed';
    if (mode === 'dashboard')
      return GRAPH_TIME_RANGE_LABELS[dashboardTimeRange] ?? dashboardTimeRange;
    const preset = config?.preset;
    if (!preset) return null;
    return UTILITY_PRESET_LABELS[preset] ?? preset;
  }

  return null;
}

export interface WidgetFrameProps {
  widget: DashboardsWidget;
  children: ReactNode;
  needsAttention?: boolean;
  className?: string;
}

/**
 * Consistent card/frame wrapper for dashboard widgets.
 * Provides consistent styling, title display, and attention state.
 */
export function WidgetFrame({
  widget,
  children,
  needsAttention = false,
  className,
}: WidgetFrameProps) {
  const dashboardTimeRange = useDashboardTimeRange();
  const timeRangeLabel = getEffectiveTimeRange(widget, dashboardTimeRange);

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-xl border bg-white transition-colors',
        'dark:bg-[#18181b]',
        needsAttention
          ? 'border-amber-500/40 bg-amber-500/[0.02] dark:border-amber-500/30'
          : 'border-gray-200 dark:border-white/[0.06]',
        className
      )}
    >
      {/* Header - compact styling to minimize visual weight */}
      {widget.title && (
        <div className="flex items-center justify-between border-b border-gray-100/50 pl-3 pr-2 dark:border-white/[0.03]">
          <h3 className="py-1.5 text-xs font-medium text-gray-500 dark:text-zinc-500">
            {widget.title}
          </h3>
          <div
            className={cn(
              'ml-2 flex items-center gap-2 py-1.5 self-stretch',
              timeRangeLabel ? 'border-l border-gray-200 pl-2 dark:border-white/[0.08]' : null
            )}
          >
            {timeRangeLabel ? (
              <span className="text-[10px] font-semibold leading-none text-gray-500 dark:text-zinc-400">
                {timeRangeLabel}
              </span>
            ) : null}
            {needsAttention && (
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
            )}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <ErrorBoundary compact>{children}</ErrorBoundary>
      </div>
    </div>
  );
}
